import i18n from 'i18next';
import * as yup from 'yup';

import type { IResourceDatabaseField, IResourceDatabaseFieldType } from '@/api/types';

import { ResourceDatabaseFieldTypeId } from '@/api/enums';

const getDatabaseFieldSchema = (
  field: IResourceDatabaseField,
  fieldTypeDict: IResourceDatabaseFieldType[],
): Record<string, yup.AnySchema> => {
  switch (field.rtxtype_Id) {
    case ResourceDatabaseFieldTypeId.Checkbox:
    case ResourceDatabaseFieldTypeId.Calculated: {
      return {};
    }

    case ResourceDatabaseFieldTypeId.FileAttachment: {
      let schema = yup.array();

      if (field.required) schema = schema.min(1, i18n.t('common.labelThisFieldIsRequired'));

      return { [field.columnName]: schema };
    }
    case ResourceDatabaseFieldTypeId.Email:
    case ResourceDatabaseFieldTypeId.Phone:
    case ResourceDatabaseFieldTypeId.WebLink: {
      let schema = yup.string();
      const validationString = fieldTypeDict.find(type => type.id === field.rtxtype_Id)?.dataValidationRegex;

      if (!validationString) return {};
      const validationRegex = new RegExp(validationString);

      schema = schema.test(
        'regex-validation',
        i18n.t(`resources.databases.fieldValidation.${field.rtxtype_Id}`),
        value => (value && value.length > 0 ? validationRegex.test(value) : true),
      );

      if (field.required) schema = schema.required(i18n.t('common.labelThisFieldIsRequired'));

      return { [field.columnName]: schema };
    }
    case ResourceDatabaseFieldTypeId.DateTime: {
      let schema = yup.string();

      if (field.required) schema = schema.required(i18n.t('common.labelThisFieldIsRequired'));

      return { [`${field.columnName}-date`]: schema, [`${field.columnName}-time`]: schema };
    }
    default:
      let schema = yup.string();

      if (field.required) schema = schema.required(i18n.t('common.labelThisFieldIsRequired'));

      return { [field.columnName]: schema };
  }
};

export const createDatabaseItemResolver = (
  fields: IResourceDatabaseField[],
  fieldTypeDict: IResourceDatabaseFieldType[],
): yup.AnyObjectSchema => {
  const properties = fields
    .filter(field => !field?.isInternal)
    .reduce<Record<string, yup.AnySchema>>((shape, field) => {
      return { ...shape, ...getDatabaseFieldSchema(field, fieldTypeDict) };
    }, {});

  return yup.object().shape(properties);
};
